import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { formValueSelector, getFormValues } from 'redux-form';
import { push } from 'react-router-redux';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import { setSelectedPremix } from '../../selected/selected.actions';
import { LoadersNames } from '../../ui/ui.constants';
import { getFarmsListRequest } from '../farms/farms.actions';
import * as selectedSelector from '../../selected/selected.selector';
import { getSelectedPremixId } from '../../selected/selected.selector';
import api from '../../../core/api';
import { createPremix, deletePremix, editPremix, getPremix, getPremixList, renamePremix } from './premixes.actions';
import { Forms } from '../../form/form.selector';
import {
  CreateRationPayload,
  EntryMethodsKeys,
  IngredientInPremixCreation,
  TargetWeights,
} from '../../application/recipes/appRecipes.types';
import { appPremixesSelector, ingredientsSelector } from '../../selectors';
import { editIngredientRequest, editIngredientTolerance } from '../ingredients/ingredients.actions';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { productRoutesPaths } from '../../../routes/ManageProducts/productRoutesPaths';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { groupActions } from '../../actions';
import { compose403ErrorMessage } from '../../../core/helpers/utils/sagaUtils';

const composeCreationPayload = (
  formValues: any,
  ingredientsInPremix: IngredientInPremixCreation[],
  method: EntryMethodsKeys,
  targetWeightType: TargetWeights,
  waterId: string,
): CreateRationPayload => {
  const ingredients = ingredientsInPremix.map(
    (ing) =>
      ({
        id: ing.ownId,
        ingredientId: ing.id,
        amounts:
          method === EntryMethodsKeys.WEIGHT && (targetWeightType === TargetWeights.ACTUAL || ing.id === waterId)
            ? [ing.actualLoad]
            : undefined,
        dryAmounts:
          method === EntryMethodsKeys.WEIGHT && targetWeightType === TargetWeights.DRY && ing.id !== waterId
            ? [ing.dryMatter]
            : undefined,
        loadPercs:
          method === EntryMethodsKeys.PERCENTAGE && (targetWeightType === TargetWeights.ACTUAL || ing.id === waterId)
            ? [ing.actualLoadTotalPercentage]
            : undefined,
        dryLoadPercs:
          method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY && ing.id !== waterId
            ? [ing.dryMatterTotalPercentage]
            : undefined,
        mixingValue: ing.mixerData.mixingValue,
        mixingTypeCode: ing.mixerData.mixingTypeCode,
        mixingStartTypeCode: ing.mixerData.mixingStartTypeCode,
        mixingRPM: ing.mixerData.mixingRPM,
        mixingStartValue: ing.mixerData.mixingStartValue,
      } as any),
  );
  return {
    displayName: formValues.displayName,
    description: formValues.description,
    density: formValues.density,
    adjOnDM: formValues.adjOnDM,
    notInUse: formValues.notInUse,
    autoAdjWater: formValues.autoAdjWater,
    percEntryMethod: targetWeightType === TargetWeights.DRY ? 1 : 2,
    amountDays: [0],
    ingredients,
    premix: true,
    targetDMPerc: formValues.autoAdjWater ? Number(formValues.targetDMPerc) : 0,
    premixAllowDMChangeAtMixer: formValues.premixAllowDMChangeAtMixer,
  };
};

export function* getPremixListSaga() {
  yield put(showLoader(LoadersNames.fetchPremixes));
  try {
    const result = yield call(api.premixes.getPremixList);
    yield put(
      getPremixList.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchPremixes));
}

export function* getPremixSaga({ payload }: ReturnType<typeof getPremix.started>) {
  yield put(showLoader(LoadersNames.getPremix));
  try {
    const premixId = yield select(getSelectedPremixId);
    if (premixId !== null) {
      const result = yield call(api.premixes.getPremix, premixId ? premixId : payload.id);

      yield put(
        getPremix.done({
          params: {},
          result,
        }),
      );
      if (payload.onSuccess) {
        payload.onSuccess();
      }
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getPremix));
}

export function* createPremixSaga(action: ReturnType<typeof createPremix.started>) {
  yield put(showLoader(LoadersNames.createPremix));
  try {
    const formName = action.payload;
    const premixCreationFormValues = yield select(getFormValues(Forms.premixCreationForm));
    const premixIngredientsFormvValues = yield select(getFormValues(Forms.premixIngredientsForm));
    const ingredientsInPremix: IngredientInPremixCreation[] = yield select(appPremixesSelector.getIngredientsInPremix);
    const method = yield select(appPremixesSelector.getMethod);
    const targetActualLoadWeight = yield select(appPremixesSelector.getTargetActualLoadWeight);
    const targetDryMatterWeight = yield select(appPremixesSelector.getTargetDryMatterWeight);
    const waterId = yield select(ingredientsSelector.getWaterId);
    const targetWeightType = premixIngredientsFormvValues?.targetWeightType;
    const payload = composeCreationPayload(
      premixCreationFormValues,
      ingredientsInPremix,
      method,
      targetWeightType,
      waterId,
    );

    const { zoneId } = premixCreationFormValues;

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL) {
      payload.amounts = [targetActualLoadWeight];
    }

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY) {
      payload.dryAmounts = [targetDryMatterWeight];
    }

    const result = yield call(api.premixes.createPremix, payload, formName);

    yield put(setSelectedPremix(result.id));

    yield put(
      groupActions.editGroup.started(
        {
          data: {
            id: result.premixGroupId,
            zoneId,
            toleranceWeight: premixCreationFormValues.deliveryToleranceWeight,
            showOnWidget: premixCreationFormValues.deliverShowOnWidget,
          } as any,
          formName,
        },
        {
          silentSuccess: true,
          withNoRedirect: true,
        },
      ),
    );

    yield put(
      editIngredientTolerance.started(
        {
          ingredientId: result.premixIngredientId,
          formName,
          toleranceWeight: premixCreationFormValues.loadingToleranceWeight,
          showOnWidget: premixCreationFormValues.loadShowOnWidget,
        },
        {
          silentSuccess: true,
        },
      ),
    );

    yield put(
      createPremix.done({
        params: formName,
        result,
      }),
    );
    yield put(notificationSuccess('products:add_premix_success'));
    yield put(push(productRoutesPaths.premixes));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createPremix));
}

export function* editPremixSaga(action: ReturnType<typeof editPremix.started>) {
  yield put(showLoader(LoadersNames.editPremix));
  try {
    const formName = action.payload;
    const premixCreationFormValues = yield select(getFormValues(Forms.premixCreationForm));
    const premixIngredientsFormvValues = yield select(getFormValues(Forms.premixIngredientsForm));
    const ingredientsInPremix: IngredientInPremixCreation[] = yield select(appPremixesSelector.getIngredientsInPremix);
    const method = yield select(appPremixesSelector.getMethod);
    const targetActualLoadWeight = yield select(appPremixesSelector.getTargetActualLoadWeight);
    const targetDryMatterWeight = yield select(appPremixesSelector.getTargetDryMatterWeight);
    const waterId = yield select(ingredientsSelector.getWaterId);
    const targetWeightType = premixIngredientsFormvValues?.targetWeightType;
    const payload = composeCreationPayload(
      premixCreationFormValues,
      ingredientsInPremix,
      method,
      targetWeightType,
      waterId,
    );

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL) {
      payload.amounts = [targetActualLoadWeight];
    }

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY) {
      payload.dryAmounts = [targetDryMatterWeight];
    }

    const { zoneId } = premixCreationFormValues;

    const premixId = yield select(selectedSelector.getSelectedPremixId);

    const result = yield call(api.premixes.editPremix, premixId, payload, formName);

    yield put(
      groupActions.editGroup.started(
        {
          data: {
            id: result.premixGroupId,
            zoneId,
            toleranceWeight: premixCreationFormValues.deliveryToleranceWeight,
            showOnWidget: premixCreationFormValues.deliverShowOnWidget,
          } as any,
          formName,
        },
        {
          silentSuccess: true,
          withNoRedirect: true,
        },
      ),
    );

    yield put(
      editIngredientTolerance.started(
        {
          ingredientId: result.premixIngredientId,
          formName,
          toleranceWeight: premixCreationFormValues.loadingToleranceWeight,
          showOnWidget: premixCreationFormValues.loadShowOnWidget,
        },
        {
          silentSuccess: true,
        },
      ),
    );

    yield put(
      editPremix.done({
        params: formName,
        result,
      }),
    );
    yield put(notificationSuccess('products:edit_premix_success'));
    yield put(push(productRoutesPaths.premixes));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editPremix));
}

export function* renamePremixSaga(action: ReturnType<typeof renamePremix.started>) {
  yield put(showLoader(LoadersNames.renamePremix));
  try {
    const formName = action.payload;
    const premixRenameFormValue = yield select(formValueSelector(Forms.premixRenameForm), 'description');

    const premixId = yield select(selectedSelector.getSelectedPremixId);
    const result = yield call(api.premixes.editPremix, premixId, { description: premixRenameFormValue }, formName);

    yield put(
      editPremix.done({
        params: formName,
        result,
      }),
    );
    yield put(notificationSuccess('products:rename_premix_success'));
    yield put(push(productRoutesPaths.premixes));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.renamePremix));
}

export function* deletePremixSaga(action: ReturnType<typeof deletePremix.started>) {
  try {
    const formName = action.payload;
    const premixId = yield select(getSelectedPremixId);
    yield call(api.premixes.deletePremix, premixId, formName);
    yield put(
      deletePremix.done({
        params: formName,
        result: premixId,
      }),
    );
    yield put(notificationSuccess('products:delete_premix_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchPremixesSaga() {
  yield all([
    yield takeLatest([setSelectedPremix, getPremix.started], getPremixSaga),
    yield takeLatest(editIngredientRequest.done, getPremixListSaga),
    yield takeLatest(deletePremix.started, deletePremixSaga),
    yield takeLatest(createPremix.started, createPremixSaga),
    yield takeLatest(editPremix.started, editPremixSaga),
    yield takeLatest(renamePremix.started, renamePremixSaga),
    yield takeLatest(getPremixList.started, getPremixListSaga),
    yield takeLatest(getFarmsListRequest.done, getPremixListSaga),
  ]);
}
